import type { AppLocale } from './numerals'

/* ============================================================================
   reference.ts — choosing the language of a piece of DATA.

   Screen chrome is translated in strings.ts. Reference data — a room type, a task
   type, a shift — is translated in the database, because the client owns the Arabic
   (confirmed with them: "Arabic would come from our system"). Both halves have to be
   right or a screen is bilingual only as far as its buttons.

   The fallback is the Latin name, never an empty string: a room type nobody has
   translated yet should read "Twin" inside an Arabic card — visibly untranslated, and
   reportable — rather than rendering a blank line that looks like missing data.

   Every place a name is drawn goes through here, so "which language is this word in"
   is one decision rather than one per component.
   ========================================================================= */

export function nameIn(
  locale: AppLocale,
  latin: string,
  arabic: string | null | undefined,
): string {
  if (locale !== 'ar') return latin
  const trimmed = arabic?.trim()
  return trimmed ? trimmed : latin
}

/* ── the unit of a stock item ────────────────────────────────────────────────
   "piece", "pair", "set" — and they arrive in English, because `StockItem.Unit` is a
   display string with no Arabic column beside it. Drawn straight, they put Latin words
   in the middle of every row of an Arabic stock table, which is the same failure as an
   untranslated status: the screen is bilingual as far as its buttons and no further.
   Found by looking at the rendered W-106 in Arabic (#74).

   THIS IS A CLOSED SET AND A SHORT ONE, so it is translated here rather than by adding
   a UnitAr column: a unit is a word about measurement, not client-authored content, and
   the hotel has no more business authoring the Arabic for "pair" than for "Save".

   THE FALLBACK IS THE ORIGINAL WORD, never a blank — a unit nobody has translated reads
   as visibly untranslated, and gets reported, rather than as a missing value. */
const UNITS: Record<string, string> = {
  piece: 'قطعة',
  pair: 'زوج',
  set: 'طقم',
  item: 'صنف',
  bottle: 'زجاجة',
  roll: 'لفة',
  pack: 'عبوة',
}

export function unitIn(locale: AppLocale, unit: string): string {
  if (locale !== 'ar') return unit
  return UNITS[unit.trim().toLowerCase()] ?? unit
}
